// Katalog Data Master — daftar pilihan yang diatur pemilik usaha dan tersimpan di tabel master_items (satu baris per
// pilihan). Kode hanya menyimpan JENIS daftar & bentuk isiannya; isinya selalu dari database (menu Data Master).

export interface MasterField {
  key: string;
  label: string;
  type: 'text' | 'number' | 'choice';
  /** Pilihan untuk type 'choice' (nilai pertama = bawaan). */
  options?: readonly { value: string; label: string }[];
  required?: boolean;
  min?: number;
  max?: number;
  decimal?: boolean;
  maxLength?: number;
  hint?: string;
}

export interface MasterKindDef {
  kind: string;
  label: string;
  /** Kelompok tab di menu Data Master. */
  group: string;
  hint: string;
  /** Isian tambahan selain nama (disimpan di kolom extra). */
  fields?: readonly MasterField[];
  /** Nama tersusun dari isian (mis. rekening) — kolom nama tidak diketik. */
  composeName?: (extra: Record<string, unknown>) => string;
}

export const MASTER_KINDS = [
  {
    kind: 'unit',
    label: 'Satuan',
    group: 'Produk',
    hint: 'Satuan produk (pcs, bal, krt, …) dan langkah jumlah di form pesan.',
    fields: [
      {
        key: 'add_step',
        label: 'Jumlah saat produk ditambahkan',
        type: 'number',
        min: 0.001,
        max: 100000,
        decimal: true,
        hint: 'mis. 10 untuk pcs',
      },
      { key: 'step', label: 'Langkah tombol − / +', type: 'number', min: 0.001, max: 100000, decimal: true },
    ],
  },
  {
    kind: 'division',
    label: 'Divisi produk',
    group: 'Produk',
    hint: 'Dipakai skema diskon, pengelompokan nota (Total 1, Total 2, …) dan kolom Qty S/O di Surat Jalan Tagihan.',
    fields: [
      {
        key: 'kolom_sj',
        label: 'Kolom Qty di Surat Jalan Tagihan',
        type: 'choice',
        options: [
          { value: 'O', label: 'O — Other (lainnya)' },
          { value: 'S', label: 'S — Selai' },
        ],
      },
    ],
  },
  {
    kind: 'rekap_group',
    label: 'Kelompok rekap',
    group: 'Produk',
    hint: 'Judul kelompok kolom di Rekapitulasi muat (mis. MACARONI, COKLAT).',
  },
  { kind: 'area', label: 'Area', group: 'Toko & rute', hint: 'Wilayah toko dan rute kirim; dipakai laporan per area.' },
  {
    kind: 'bank',
    label: 'Rekening transfer',
    group: 'Keuangan',
    hint: 'Rekening tujuan transfer — tampil di nota & aplikasi toko, dipilih saat mencatat transfer.',
    fields: [
      { key: 'bank', label: 'Bank', type: 'text', required: true, maxLength: 40 },
      { key: 'number', label: 'Nomor rekening', type: 'text', required: true, maxLength: 40 },
      { key: 'holder', label: 'Atas nama', type: 'text', required: true, maxLength: 80 },
    ],
    composeName: (x) => `${String(x.bank ?? '').trim()} ${String(x.number ?? '').trim()} a.n. ${String(x.holder ?? '').trim()}`,
  },
  { kind: 'expense_category', label: 'Kategori biaya', group: 'Keuangan', hint: 'Pilihan kategori di menu Biaya.' },
  {
    kind: 'visit_reason',
    label: 'Alasan tidak order',
    group: 'Daftar alasan',
    hint: 'Dipilih sales saat kunjungan tanpa order.',
  },
  { kind: 'fail_reason', label: 'Alasan gagal kirim', group: 'Daftar alasan', hint: 'Dipilih driver/admin saat gagal kirim.' },
  {
    kind: 'short_reason',
    label: 'Alasan barang kurang',
    group: 'Daftar alasan',
    hint: 'Dipilih driver bila barang yang diterima toko kurang dari nota.',
  },
  { kind: 'stock_source', label: 'Sumber barang masuk', group: 'Daftar alasan', hint: 'Dipilih saat mencatat barang masuk.' },
  {
    kind: 'adjust_reason',
    label: 'Alasan penyesuaian stok',
    group: 'Daftar alasan',
    hint: 'Dipilih saat stok opname / penyesuaian.',
  },
] as const satisfies readonly MasterKindDef[];

export type MasterKind = (typeof MASTER_KINDS)[number]['kind'];
export const MASTER_KIND_KEYS: readonly MasterKind[] = MASTER_KINDS.map((k) => k.kind);
const BY_KIND = new Map<string, MasterKindDef>(MASTER_KINDS.map((k) => [k.kind, k]));
export const masterKind = (k: string): MasterKindDef | undefined => BY_KIND.get(k);
export const MASTER_GROUPS: string[] = [...new Set(MASTER_KINDS.map((k) => k.group))];

/** Satu pilihan aktif seperti dikirim /api/meta. */
export interface MasterItem {
  id: number;
  kind: MasterKind;
  name: string;
  extra: Record<string, unknown>;
}

/** Nama pilihan aktif satu jenis (urut sesuai Data Master). Nilai `keep` (data lama yang pilihannya sudah dinonaktifkan)
 *  tetap disertakan agar form ubah tidak kehilangan nilainya. */
export function choiceNames(items: readonly MasterItem[] | undefined, kind: MasterKind, keep?: string | null): string[] {
  const names = (items ?? []).filter((x) => x.kind === kind).map((x) => x.name);
  const k = String(keep ?? '').trim();
  return k && !names.some((n) => n.toLowerCase() === k.toLowerCase()) ? [...names, k] : names;
}

/** Langkah jumlah satuan dari Data Master: { add: saat ditambahkan, step: tombol −/+ } (bawaan 1). */
export function unitStep(items: readonly MasterItem[] | undefined, unit: string): { add: number; step: number } {
  const u = (items ?? []).find((x) => x.kind === 'unit' && x.name.toLowerCase() === String(unit).trim().toLowerCase());
  const add = Number(u?.extra.add_step);
  const step = Number(u?.extra.step);
  const a = Number.isFinite(add) && add > 0 ? add : 1;
  return { add: a, step: Number.isFinite(step) && step > 0 ? step : a };
}

/** Validasi isian satu pilihan; kembalikan { name, extra } yang sudah rapi atau pesan error. */
export function validateMasterItem(
  kind: string,
  body: { name?: unknown; extra?: unknown },
): { ok: true; name: string; extra: Record<string, unknown> } | { ok: false; error: string } {
  const d = masterKind(kind);
  if (!d) return { ok: false, error: 'Jenis data master tidak dikenal.' };
  const src = (body.extra && typeof body.extra === 'object' ? body.extra : {}) as Record<string, unknown>;
  const extra: Record<string, unknown> = {};
  for (const f of d.fields ?? []) {
    const raw = String(src[f.key] ?? '').trim();
    if (!raw) {
      if (f.required) return { ok: false, error: `${f.label} wajib diisi.` };
      continue;
    }
    if (f.type === 'choice') {
      if (!(f.options ?? []).some((o) => o.value === raw)) return { ok: false, error: `${f.label} tidak valid.` };
      extra[f.key] = raw;
    } else if (f.type === 'number') {
      const n = Number(raw.replace(',', '.'));
      if (!Number.isFinite(n) || (!f.decimal && !Number.isInteger(n))) return { ok: false, error: `${f.label} harus angka.` };
      if ((f.min !== undefined && n < f.min) || (f.max !== undefined && n > f.max))
        return { ok: false, error: `${f.label} harus ${f.min}–${f.max}.` };
      extra[f.key] = n;
    } else {
      if (f.maxLength && raw.length > f.maxLength) return { ok: false, error: `${f.label} maksimal ${f.maxLength} karakter.` };
      extra[f.key] = raw;
    }
  }
  const name = (d.composeName ? d.composeName(extra) : String(body.name ?? '')).trim().replace(/\s+/g, ' ');
  if (!name) return { ok: false, error: 'Nama wajib diisi.' };
  if (name.length > 120) return { ok: false, error: 'Nama maksimal 120 karakter.' };
  return { ok: true, name, extra };
}

/** Slot foto produk (maks. 3) — tampak depan, luar (kemasan), dalam (isi). */
export const PRODUCT_PHOTO_SLOTS = ['Tampak depan', 'Tampak luar (kemasan)', 'Tampak dalam (isi)'] as const;
export interface ProductPhoto {
  slot: number;
  file: string;
}
